import React, { FC, LegacyRef } from "react";
import classNames from "classnames";
import { useI18next } from "gatsby-plugin-react-i18next";
import { Link } from "@app/components/common/link/Link";
import { INavigationMenuItem } from "@app/models/storyblok/datasource";

type Props = {
  data: INavigationMenuItem[];
  activeMenuGroup: string;
  dropdownRef: LegacyRef<HTMLDivElement>;
  isNotificationBarEnabled: boolean;
  isTopShift: boolean;
  setActiveMenuGroup: (menuGroup: string) => void;
};

export const FullNavigationDropdownMenu: FC<Props> = ({
  data,
  activeMenuGroup,
  dropdownRef,
  isNotificationBarEnabled,
  isTopShift,
  setActiveMenuGroup,
}) => {
  const { language, defaultLanguage } = useI18next();
  const menuGroupsWithChildren = data.filter(
    (menuGroup) => menuGroup.groups?.length,
  );

  return (
    <>
      {menuGroupsWithChildren.map((menuGroup) => (
        <div
          className={classNames(
            "text-base-content fixed right-4 left-4 z-10 transition-all duration-300",
            "lg:right-7.5 lg:left-7.5",
            {
              "visible opacity-100": menuGroup.id === activeMenuGroup,
              "invisible opacity-0": menuGroup.id !== activeMenuGroup,
              "top-[8.25rem] lg:top-[9.125rem]": isNotificationBarEnabled,
              "top-22.5 lg:top-[6.675rem]": !isNotificationBarEnabled,
              "-translate-y-10": isNotificationBarEnabled && isTopShift,
            },
          )}
          onMouseEnter={() => setActiveMenuGroup(activeMenuGroup)}
          key={menuGroup.id}
          ref={menuGroup.id === activeMenuGroup ? dropdownRef : null}
        >
          <div
            className={classNames(
              "bg-nav/88 relative mt-3 flex gap-20 rounded-2xl p-10 backdrop-blur-md",
            )}
          >
            <h5 className={classNames("text-large block w-[235px] flex-none")}>
              {menuGroup.title[language] ?? menuGroup.title[defaultLanguage]}
            </h5>
            <div className={classNames("flex grow gap-20")}>
              {menuGroup.groups.map((group) => (
                <nav key={group.id} className="space-y-4">
                  <h6
                    className={classNames(
                      "bg-nav-label w-fit rounded-full px-2.5 py-0.5 text-[12px] leading-[18px]",
                    )}
                  >
                    {group.groupName[language] ??
                      group.groupName[defaultLanguage]}
                  </h6>
                  <ul className={classNames("text-regular space-y-4")}>
                    {group.entries.map((entry) => (
                      <li key={entry.id}>
                        <Link
                          to={entry.ref}
                          className="hover:text-base-content/50 no-underline duration-300"
                          onClick={() => setActiveMenuGroup(null)}
                        >
                          <span>
                            {entry.name[language] ??
                              entry.name[defaultLanguage]}
                          </span>
                          {entry.subtext ? (
                            <p
                              className={classNames(
                                "text-extra-small text-nav-secondary mb-2",
                              )}
                            >
                              {entry.subtext[language] ??
                                entry.subtext[defaultLanguage]}
                            </p>
                          ) : null}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </nav>
              ))}
            </div>
          </div>
        </div>
      ))}
    </>
  );
};
